import React, {
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Avatar,
  Box,
  Button,
  Container,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ThemeProvider,
  Typography,
  createTheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import * as XLSX from 'xlsx';
import { pageColors } from './HomePage';
import apiFetch from '../api/apiFetch';

const ARTICLES_THEME = createTheme({
  palette: {
    primary: {
      main: pageColors.articles,
    },
  },
  typography: {
    fontFamily: 'Inter, Roboto, sans-serif',
  },
});

const PAGE_BACKGROUND =
  'radial-gradient(circle at top left, rgba(81,45,168,0.16), transparent 55%), #f5f7fb';

const SURFACE_SX = {
  borderRadius: 4,
  backgroundColor: '#fff',
  boxShadow: '0 18px 40px rgba(0,0,0,0.07)',
  border: '1px solid rgba(0,0,0,0.05)',
};

const FILTER_SUMMARY_ITEM_SX = {
  minWidth: 0,
  px: 1,
  py: 0.75,
  borderRadius: 2,
  border: '1px solid rgba(0,0,0,0.08)',
  backgroundColor: 'rgba(81,45,168,0.05)',
};

const TEXT_FILTER_FIELD_SX = {
  width: { xs: '100%', sm: 320 },
  flex: { sm: '0 0 auto' },
};

const TABLE_HEADER_SX = {
  backgroundColor: '#fafafa',
  whiteSpace: 'nowrap',
  fontWeight: 700,
};

const TABLE_ROW_HOVER_SX = {
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.03)',
  },
};

const HIGHLIGHTED_ROW_SX = {
  backgroundColor: '#e8f5e9',
  '&:hover': {
    backgroundColor: '#dff0e3',
  },
};

function hasValue(value) {
  return value !== null && value !== undefined && value !== '';
}

function formatCellValue(value) {
  return hasValue(value) ? value : '-';
}

function formatPercentValue(value, emptyFallback = '-') {
  if (!hasValue(value)) {
    return emptyFallback;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? `${numericValue.toFixed(1)}%` : emptyFallback;
}

function compareSortValues(valueA, valueB, order) {
  const aEmpty = !hasValue(valueA);
  const bEmpty = !hasValue(valueB);

  if (aEmpty && !bEmpty) return 1;
  if (!aEmpty && bEmpty) return -1;
  if (aEmpty && bEmpty) return 0;

  const numericValueA = Number(valueA);
  const numericValueB = Number(valueB);
  const bothNumeric = Number.isFinite(numericValueA) && Number.isFinite(numericValueB);

  if (bothNumeric) {
    return order === 'asc'
      ? numericValueA - numericValueB
      : numericValueB - numericValueA;
  }

  const stringValueA = String(valueA).toLowerCase();
  const stringValueB = String(valueB).toLowerCase();

  if (stringValueA < stringValueB) return order === 'asc' ? -1 : 1;
  if (stringValueA > stringValueB) return order === 'asc' ? 1 : -1;

  return 0;
}

function getArticlesLoadErrorMessage(status) {
  if (status === 401) {
    return 'Lost connection to Fortnox. Please reconnect and try again.';
  }

  if (status === 429) {
    return 'Fortnox cannot handle requests right now. Please wait a moment and try again.';
  }

  return 'Failed to load articles. Please try again.';
}

function buildArticleRow(article, index) {
  const articleNumber = article?.ArticleNumber ?? '';
  const description = article?.Description ?? '';
  const supplierName = article?.SupplierName ?? '';
  const purchasePrice = article?.PurchasePrice;
  const salesPrice = article?.SalesPrice;
  const marginWithoutFreight = article?.MarginWithoutFreight;
  const marginWithFreight = article?.MarginWithFreight;
  const topSellerRank = article?.topSellerRank;

  return {
    key: articleNumber || `article-${index}`,
    articleNumber,
    articleNumberDisplay: formatCellValue(articleNumber),
    articleNumberSort: articleNumber,
    description,
    descriptionDisplay: formatCellValue(description),
    descriptionSearch: description.trim().toLowerCase(),
    supplierName,
    supplierNameDisplay: formatCellValue(supplierName),
    supplierNameSearch: supplierName.trim().toLowerCase(),
    purchasePrice,
    purchasePriceDisplay: formatCellValue(purchasePrice),
    salesPrice,
    salesPriceDisplay: formatCellValue(salesPrice),
    marginWithoutFreight,
    marginWithoutFreightDisplay: formatPercentValue(marginWithoutFreight),
    marginWithFreight,
    marginWithFreightDisplay: formatPercentValue(marginWithFreight),
    topSellerRank,
    topSellerRankDisplay: hasValue(topSellerRank) ? topSellerRank : '-',
    isHighlighted: hasValue(topSellerRank) && Number(topSellerRank) <= 20,
  };
}

const ArticlesResultsTable = memo(function ArticlesResultsTable({
  hasLoadedData,
  isMobile,
  onRequestSort,
  order,
  orderBy,
  rows,
}) {
  const renderedRows = useMemo(() => {
    if (rows.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={isMobile ? 3 : 8} align="center" sx={{ py: 4 }}>
            {hasLoadedData
              ? 'No articles match the current filter.'
              : 'No articles to display. Cache may be empty or connection issue.'}
          </TableCell>
        </TableRow>
      );
    }

    return rows.map((row) => (
      <TableRow
        key={row.key}
        hover
        sx={row.isHighlighted ? HIGHLIGHTED_ROW_SX : TABLE_ROW_HOVER_SX}
      >
        {isMobile ? (
          <>
            <TableCell>{row.descriptionDisplay}</TableCell>
            <TableCell>{row.marginWithFreightDisplay}</TableCell>
            <TableCell>{row.topSellerRankDisplay}</TableCell>
          </>
        ) : (
          <>
            <TableCell>{row.articleNumberDisplay}</TableCell>
            <TableCell>{row.descriptionDisplay}</TableCell>
            <TableCell>{row.supplierNameDisplay}</TableCell>
            <TableCell>{row.purchasePriceDisplay}</TableCell>
            <TableCell>{row.salesPriceDisplay}</TableCell>
            <TableCell>{row.marginWithoutFreightDisplay}</TableCell>
            <TableCell>{row.marginWithFreightDisplay}</TableCell>
            <TableCell>{row.topSellerRankDisplay}</TableCell>
          </>
        )}
      </TableRow>
    ));
  }, [hasLoadedData, isMobile, rows]);

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        ...SURFACE_SX,
        maxHeight: '72vh',
        overflowY: 'auto',
        overflowX: { xs: 'hidden', sm: 'auto' },
      }}
    >
      <Table
        stickyHeader
        sx={{
          tableLayout: 'fixed',
          width: '100%',
          minWidth: { xs: 'unset', sm: 'auto' },
        }}
        aria-label="articles table"
      >
        <TableHead>
          <TableRow>
            {isMobile ? (
              <>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '60%' }}>
                  <TableSortLabel
                    active={orderBy === 'Description'}
                    direction={orderBy === 'Description' ? order : 'asc'}
                    onClick={() => onRequestSort('Description')}
                  >
                    Article Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '20%' }}>
                  <TableSortLabel
                    active={orderBy === 'MarginWithFreight'}
                    direction={orderBy === 'MarginWithFreight' ? order : 'asc'}
                    onClick={() => onRequestSort('MarginWithFreight')}
                  >
                    Margin
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '20%' }}>
                  <TableSortLabel
                    active={orderBy === 'topSellerRank'}
                    direction={orderBy === 'topSellerRank' ? order : 'asc'}
                    onClick={() => onRequestSort('topSellerRank')}
                  >
                    Rank
                  </TableSortLabel>
                </TableCell>
              </>
            ) : (
              <>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '7%' }}>
                  <TableSortLabel
                    active={orderBy === 'ArticleNumber'}
                    direction={orderBy === 'ArticleNumber' ? order : 'asc'}
                    onClick={() => onRequestSort('ArticleNumber')}
                  >
                    Art. ID
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '15%' }}>
                  <TableSortLabel
                    active={orderBy === 'Description'}
                    direction={orderBy === 'Description' ? order : 'asc'}
                    onClick={() => onRequestSort('Description')}
                  >
                    Article Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '15%' }}>
                  <TableSortLabel
                    active={orderBy === 'SupplierName'}
                    direction={orderBy === 'SupplierName' ? order : 'asc'}
                    onClick={() => onRequestSort('SupplierName')}
                  >
                    Supplier Name
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '13%' }}>
                  <TableSortLabel
                    active={orderBy === 'PurchasePrice'}
                    direction={orderBy === 'PurchasePrice' ? order : 'asc'}
                    onClick={() => onRequestSort('PurchasePrice')}
                  >
                    Purchase Price
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '13%' }}>
                  <TableSortLabel
                    active={orderBy === 'SalesPrice'}
                    direction={orderBy === 'SalesPrice' ? order : 'asc'}
                    onClick={() => onRequestSort('SalesPrice')}
                  >
                    Sales Price
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '17%' }}>
                  <TableSortLabel
                    active={orderBy === 'MarginWithoutFreight'}
                    direction={orderBy === 'MarginWithoutFreight' ? order : 'asc'}
                    onClick={() => onRequestSort('MarginWithoutFreight')}
                  >
                    Margin (no freight)
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '10%' }}>
                  <TableSortLabel
                    active={orderBy === 'MarginWithFreight'}
                    direction={orderBy === 'MarginWithFreight' ? order : 'asc'}
                    onClick={() => onRequestSort('MarginWithFreight')}
                  >
                    Margin
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ ...TABLE_HEADER_SX, width: '10%' }}>
                  <TableSortLabel
                    active={orderBy === 'topSellerRank'}
                    direction={orderBy === 'topSellerRank' ? order : 'asc'}
                    onClick={() => onRequestSort('topSellerRank')}
                  >
                    Rank
                  </TableSortLabel>
                </TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>{renderedRows}</TableBody>
      </Table>
    </TableContainer>
  );
});

export default function ArticlesPage({ connected, setConnected, setError, loadingConnection }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('ArticleNumber');
  const [freightPercentage, setFreightPercentage] = useState(8);
  const [articleNameFilter, setArticleNameFilter] = useState('');
  const deferredArticleNameFilter = useDeferredValue(articleNameFilter);
  const hasLoadErrorMessageRef = useRef(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchArticles = async () => {
      if (loadingConnection) return;

      if (!connected) {
        if (!hasLoadErrorMessageRef.current) {
          setError('Not connected to Fortnox. Please connect first.');
        }
        setConnected(false);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        hasLoadErrorMessageRef.current = false;
        const [articlesResponse, configResponse] = await Promise.all([
          apiFetch('/api/articles'),
          apiFetch('/api/config/freight-percentage'),
        ]);

        if (!articlesResponse.ok) {
          const errMsg = getArticlesLoadErrorMessage(articlesResponse.status);
          if (articlesResponse.status === 401) {
            setConnected(false);
          } else if (articlesResponse.status === 429) {
            setConnected(true);
          } else {
            setConnected(false);
          }
          hasLoadErrorMessageRef.current = true;
          setError(errMsg);
          return;
        }

        const articlesData = await articlesResponse.json();
        setArticles(articlesData);
        setConnected(true);
        hasLoadErrorMessageRef.current = false;

        if (configResponse.ok) {
          const configData = await configResponse.json();
          if (
            configData.freightPercentage !== null &&
            configData.freightPercentage !== undefined
          ) {
            setFreightPercentage(configData.freightPercentage);
          }
        }
      } catch (err) {
        console.error('Failed to load articles', err);
        hasLoadErrorMessageRef.current = true;
        setError(
          'Unable to load articles right now. Please check your connection and try again.'
        );
        setConnected(false);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [connected, setConnected, setError, loadingConnection]);

  const handleRequestSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    },
    [order, orderBy]
  );

  const normalizedArticleNameFilter = deferredArticleNameFilter.trim().toLowerCase();

  const articleRows = useMemo(
    () =>
      Array.isArray(articles)
        ? articles
            .filter((article) => article !== null && article !== undefined)
            .map((article, index) => buildArticleRow(article, index))
        : [],
    [articles]
  );

  const filteredRows = useMemo(() => {
    if (!normalizedArticleNameFilter) {
      return articleRows;
    }

    return articleRows.filter((row) =>
      row.descriptionSearch.includes(normalizedArticleNameFilter)
    );
  }, [articleRows, normalizedArticleNameFilter]);

  const sortedRows = useMemo(() => {
    const comparator = (rowA, rowB) => {
      switch (orderBy) {
        case 'ArticleNumber':
          return compareSortValues(rowA.articleNumberSort, rowB.articleNumberSort, order);
        case 'Description':
          return compareSortValues(rowA.descriptionSearch, rowB.descriptionSearch, order);
        case 'SupplierName':
          return compareSortValues(rowA.supplierNameSearch, rowB.supplierNameSearch, order);
        case 'PurchasePrice':
          return compareSortValues(rowA.purchasePrice, rowB.purchasePrice, order);
        case 'SalesPrice':
          return compareSortValues(rowA.salesPrice, rowB.salesPrice, order);
        case 'MarginWithoutFreight':
          return compareSortValues(
            rowA.marginWithoutFreight,
            rowB.marginWithoutFreight,
            order
          );
        case 'MarginWithFreight':
          return compareSortValues(
            rowA.marginWithFreight,
            rowB.marginWithFreight,
            order
          );
        case 'topSellerRank':
          return compareSortValues(rowA.topSellerRank, rowB.topSellerRank, order);
        default:
          return 0;
      }
    };

    return [...filteredRows].sort(comparator);
  }, [filteredRows, order, orderBy]);

  const hasLoadedData = articleRows.length > 0;
  const hasFilteredResults = sortedRows.length > 0;
  const isApplyingFilters = articleNameFilter !== deferredArticleNameFilter;

  const handleExportToExcel = () => {
    if (!hasFilteredResults) {
      setError('No data to export');
      return;
    }

    try {
      const exportData = sortedRows.map((row) => ({
        'Article ID': row.articleNumber || '',
        'Article Name': row.description || '',
        'Supplier Name': row.supplierName || '',
        'Purchase Price': row.purchasePrice ?? '',
        'Sales Price': row.salesPrice ?? '',
        'Margin (no freight)': formatPercentValue(row.marginWithoutFreight, ''),
        Margin:
          formatPercentValue(row.marginWithFreight, ''),
        Rank: row.topSellerRank ?? '',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      const colWidths = [
        { wch: 12 },
        { wch: 25 },
        { wch: 20 },
        { wch: 15 },
        { wch: 12 },
        { wch: 18 },
        { wch: 10 },
        { wch: 8 },
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Articles');

      const date = new Date().toISOString().split('T')[0];
      const filename = `articles_export_${date}.xlsx`;

      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export data to Excel');
    }
  };

  return (
    <ThemeProvider theme={ARTICLES_THEME}>
      <Box
        sx={{
          minHeight: '100vh',
          background: PAGE_BACKGROUND,
          py: { xs: 3, md: 4 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="xl">
          <Stack spacing={2.25}>
            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 2 } }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: 'rgba(81,45,168,0.14)',
                      color: pageColors.articles,
                    }}
                  >
                    <Inventory2Icon fontSize="large" />
                  </Avatar>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="overline" sx={{ color: pageColors.articles, letterSpacing: 3 }}>
                      ARTICLES
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
                      Articles
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 0.25 }}>
                      Review article pricing, supplier coverage, and ranked margins in one place.
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ ...SURFACE_SX, p: { xs: 1.5, md: 1.75 } }}>
              <Stack spacing={1.25}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Filters
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    Filter the current article list by name or export the current view.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      lg: 'minmax(0, 1fr) auto',
                    },
                    gap: 1.25,
                    alignItems: 'start',
                  }}
                >
                  <Stack spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        alignItems: 'start',
                      }}
                    >
                      <TextField
                        label="Article Name"
                        variant="outlined"
                        size="small"
                        value={articleNameFilter}
                        onChange={(event) => setArticleNameFilter(event.target.value)}
                        fullWidth
                        sx={TEXT_FILTER_FIELD_SX}
                      />

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          flex: { sm: '0 0 auto' },
                        }}
                      >
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<DownloadIcon />}
                          onClick={handleExportToExcel}
                          disabled={loading || !hasFilteredResults}
                          sx={{ minHeight: 38, minWidth: { sm: 152 } }}
                        >
                          Export to Excel
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>

                  <Box
                    sx={{
                      ...FILTER_SUMMARY_ITEM_SX,
                      width: { xs: '100%', sm: 'auto' },
                      minWidth: { sm: 240 },
                      justifySelf: { lg: 'end' },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontWeight: 700,
                        lineHeight: 1.2,
                        color: pageColors.articles,
                      }}
                    >
                      Freight Rule
                    </Typography>
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Margins use {freightPercentage}% freight on purchase price.
                    </Typography>
                  </Box>
                </Box>

                {isApplyingFilters ? (
                  <Typography variant="caption" color="text.secondary">
                    Updating filtered results...
                  </Typography>
                ) : null}
              </Stack>
            </Paper>

            {loading ? (
              <Paper elevation={0} sx={{ ...SURFACE_SX, p: 0, overflow: 'hidden' }}>
                <LinearProgress />
              </Paper>
            ) : (
              <ArticlesResultsTable
                hasLoadedData={hasLoadedData}
                isMobile={isMobile}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
                rows={sortedRows}
              />
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
